import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { useAuth } from '../auth/AuthContext';

const initialMessages = [
  {
    role: 'assistant',
    content: 'Привет. Я помогу с требованиями, конфликтами и QA-артефактами этого проекта.',
  },
];

export default function ProjectLlmChatWidget({ project, onRequirementsChanged, onUserStoriesChanged, onQaArtifactChanged, stage = 'requirements' }) {
  const { apiClient } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(initialMessages);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [pendingChange, setPendingChange] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!project?.id) return;
    apiClient.get(`/api/chat/history?projectId=${project.id}`)
      .then((response) => {
        const history = response?.data || [];
        setMessages(history.length > 0 ? history : initialMessages);
      })
      .catch(() => setMessages(initialMessages));
    apiClient.get(`/api/projects/${project.id}/requirements?page=0&size=1000`)
      .then((response) => setRequirements((response?.data?.content || []).map((item, index) => ({
        ...item,
        // This is the visible number in the project table, not the chunk-local seqNumber.
        displayNumber: index + 1,
      }))))
      .catch(() => setRequirements([]));
  }, [apiClient, project?.id]);

  const apiMessages = useMemo(() => messages
    .filter((message) => message.role === 'user' || message.role === 'assistant')
    .map((message) => ({ role: message.role, content: message.content })),
  [messages]);

  const sendMessage = async () => {
    const content = draft.trim();
    if (!content || loading) {
      return;
    }
    const nextMessages = [...apiMessages, { role: 'user', content }];
    setMessages((prev) => [...prev, { role: 'user', content }]);
    setDraft('');
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/api/chat/completions', {
        projectId: project?.id,
        stage,
        messages: [
          {
            role: 'user',
            content: `Контекст проекта: ${project?.name || 'без названия'}.`,
          },
          ...nextMessages,
        ],
      });
      const answer = response?.data?.content || 'Пустой ответ модели.';
      const action = response?.data?.action;
      if (action?.name === 'propose_requirement_change') {
        const args = action.arguments || {};
        const requirement = requirements.find((item) => (item.stableId || item.id) === args.requirementId);
        if (requirement && (args.operation === 'replace_text' || args.operation === 'set_enabled')) {
          const patch = args.operation === 'replace_text' ? { text: args.newText } : { enabled: Boolean(args.enabled) };
          setPendingChange({ requirement, patch, description: args.operation === 'replace_text'
            ? `заменить текст требования №${args.displayNumber} на «${args.newText}»`
            : `${args.enabled ? 'включить' : 'отключить'} требование №${args.displayNumber}` });
        }
      }
      if (action?.name === 'propose_user_story_change' && stage === 'user_stories') {
        const args = action.arguments || {};
        const number = Number(args.displayNumber);
        if (Number.isInteger(number) && number > 0 && (args.operation === 'delete' || args.operation === 'replace_text')) {
          const artifactResponse = await apiClient.get(`/api/projects/${project.id}/story-qa/user-stories/artifacts/latest`);
          const artifact = artifactResponse?.data;
          const lines = String(artifact?.storiesText || '').replace(/\r\n/g, '\n').split('\n').filter((line) => line.trim());
          const lineIndex = lines[0]?.startsWith('User stories для проекта') ? number : number - 1;
          if (artifact?.id && lineIndex >= 0 && lineIndex < lines.length) {
            const updatedLines = [...lines];
            if (args.operation === 'delete') updatedLines.splice(lineIndex, 1);
            else updatedLines[lineIndex] = String(args.newText || '').trim();
            setPendingChange({ kind: 'user_story', artifact, storiesText: updatedLines.join('\n'), description: args.operation === 'delete'
              ? `удалить User Story №${number}` : `заменить User Story №${number}` });
          }
        }
      }
      if (action?.name === 'propose_qa_artifact_change' && stage === 'test_cases') {
        const args = action.arguments || {};
        const responseArtifact = await apiClient.get(`/api/projects/${project.id}/story-qa/test-cases/artifacts/latest`);
        const artifact = responseArtifact?.data;
        const rows = Array.isArray(artifact?.rows) ? artifact.rows.map((row) => ({ ...row })) : [];
        const rowIndex = Number(args.rowNumber) - 1;
        if (artifact?.id && rowIndex >= 0 && rowIndex < rows.length && (args.operation === 'delete_row' || args.operation === 'replace_field')) {
          if (args.operation === 'delete_row') rows.splice(rowIndex, 1);
          else if (args.field && Object.prototype.hasOwnProperty.call(rows[rowIndex], args.field)) rows[rowIndex][args.field] = String(args.value || '');
          setPendingChange({ kind: 'test_cases', artifact, rows, description: args.operation === 'delete_row'
            ? `удалить тест-кейс №${args.rowNumber}` : `изменить поле «${args.field}» тест-кейса №${args.rowNumber}` });
        }
      }
      if (action?.name === 'propose_current_screen_change' && (stage === 'checklist' || stage === 'summary_report')) {
        const args = action.arguments || {};
        const endpoint = stage === 'checklist' ? 'checklists' : 'summary-reports';
        const responseArtifact = await apiClient.get(`/api/projects/${project.id}/story-qa/${endpoint}/artifacts/latest`);
        const artifact = responseArtifact?.data;
        const lines = String(artifact?.checklistText || artifact?.reportText || '').replace(/\r\n/g, '\n').split('\n');
        const rowCandidates = lines
          .map((line, index) => ({ line, index }))
          .filter(({ line }) => line.trim().startsWith('|') && line.split('|').length >= 4 && !/^\s*\|?\s*-{3,}/.test(line));
        const rowIndex = stage === 'checklist'
          ? rowCandidates[Number(args.rowNumber) - 1]?.index
          : Number(args.rowNumber) - 1;
        if (artifact?.id && rowIndex >= 0 && rowIndex < lines.length && (args.operation === 'delete_row' || args.operation === 'replace_text' || args.operation === 'replace_field')) {
          if (args.operation === 'delete_row') lines.splice(rowIndex, 1);
          else lines[rowIndex] = String(args.value || '');
          setPendingChange({ kind: stage, artifact, text: lines.join('\n'), endpoint, description: args.operation === 'delete_row'
            ? `удалить строку №${args.rowNumber}` : `изменить строку №${args.rowNumber}` });
        }
      }
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: answer,
      }]);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Не удалось получить ответ LLM.');
    } finally {
      setLoading(false);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const confirmChange = async () => {
    if (!pendingChange || loading) return;
    setLoading(true);
    setError(null);
    try {
      if (pendingChange.kind === 'user_story') {
        await apiClient.put(`/api/projects/${project.id}/story-qa/user-stories/artifacts/${pendingChange.artifact.id}`, { storiesText: pendingChange.storiesText });
        await onUserStoriesChanged?.();
        setMessages((prev) => [...prev, { role: 'assistant', content: `Изменение подтверждено и применено: ${pendingChange.description}.` }]);
        setPendingChange(null);
        return;
      }
      if (pendingChange.kind === 'test_cases') {
        await apiClient.put(`/api/projects/${project.id}/story-qa/test-cases/artifacts/${pendingChange.artifact.id}`, { rows: pendingChange.rows });
        await onQaArtifactChanged?.();
        setMessages((prev) => [...prev, { role: 'assistant', content: `Изменение подтверждено и применено: ${pendingChange.description}.` }]);
        setPendingChange(null);
        return;
      }
      if (pendingChange.kind === 'checklist' || pendingChange.kind === 'summary_report') {
        const body = pendingChange.kind === 'checklist' ? { checklistText: pendingChange.text } : { reportText: pendingChange.text };
        await apiClient.put(`/api/projects/${project.id}/story-qa/${pendingChange.endpoint}/artifacts/${pendingChange.artifact.id}`, body);
        await onQaArtifactChanged?.();
        setMessages((prev) => [...prev, { role: 'assistant', content: `Изменение подтверждено и применено: ${pendingChange.description}.` }]);
        setPendingChange(null);
        return;
      }
      const requirementId = pendingChange.requirement.stableId || pendingChange.requirement.id;
      await apiClient.put(`/api/chat/actions/requirements/${requirementId}`, pendingChange.patch, {
        params: { projectId: project.id },
      });
      await onRequirementsChanged?.();
      setMessages((prev) => [...prev, { role: 'assistant', content: `Изменение подтверждено и применено: ${pendingChange.description}.` }]);
      setRequirements((prev) => prev.map((item) => (item.stableId || item.id) === requirementId ? { ...item, ...pendingChange.patch } : item));
      setPendingChange(null);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Не удалось применить изменение.');
    } finally { setLoading(false); }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const widget = (
    <Box
      sx={{
        position: 'fixed',
        right: { xs: 16, md: 28 },
        bottom: { xs: 16, md: 24 },
        zIndex: (theme) => theme.zIndex.drawer + 2,
      }}
    >
      {open ? (
        <Paper
          elevation={8}
          sx={{
            width: { xs: 'calc(100vw - 32px)', sm: 390 },
            height: { xs: 480, sm: 520 },
            borderRadius: 3,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.25, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SmartToyRoundedIcon color="primary" />
              <Typography variant="subtitle1">LLM чат</Typography>
            </Stack>
            <Tooltip title="Закрыть">
              <IconButton size="small" onClick={() => setOpen(false)}>
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack spacing={1.25} sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {messages.map((message, index) => (
              <Box
                key={`${message.role}-${index}`}
                sx={{
                  alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '86%',
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  backgroundColor: message.role === 'user' ? 'primary.main' : 'background.default',
                  color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                  border: message.role === 'user' ? 'none' : (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {message.content}
                </Typography>
              </Box>
            ))}
            {loading && (
              <Typography variant="body2" color="text.secondary">
                Думаю…
              </Typography>
            )}
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {pendingChange && (
              <Alert severity="warning" action={<Button color="inherit" size="small" onClick={confirmChange} disabled={loading}>Подтвердить</Button>}>
                Подтвердить: {pendingChange.description}?
              </Alert>
            )}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ p: 1.5, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
            <TextField
              inputRef={inputRef}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Спросите по проекту"
              size="small"
              multiline
              maxRows={4}
              fullWidth
              disabled={loading}
            />
            <Tooltip title="Отправить">
              <span>
                <IconButton color="primary" onClick={sendMessage} disabled={loading || !draft.trim()}>
                  <SendRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Paper>
      ) : (
        <Button
          variant="contained"
          startIcon={<SmartToyRoundedIcon />}
          onClick={() => setOpen(true)}
          sx={{ boxShadow: '0 18px 36px rgba(233, 79, 55, 0.24)' }}
        >
          LLM чат
        </Button>
      )}
    </Box>
  );

  return createPortal(widget, document.body);
}
